import * as React from "react";
import { CheckPricesLocalizationProps, CheckPricesProps } from "./typings";
import {
  CheckPricesFlexModuleResult,
  Deal
} from "typings/microserviceModels/check-prices-flex-module";
import {
  UitkCard,
  UitkCardContent,
  UitkCardSectionSeparatorType,
  UitkCardTextSegment
} from "uitk-react-cards";
import { SearchContext } from "typings/flexFramework/FlexDefinitions";
import { ILocalization, LocalizedText, formatDateString } from "bernie-l10n";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { isPolygon } from "src/components/utility/LocationUtils";

const checkPricesKey = new ItemKeyHelper("check-prices");
export const modelKeyToDealTextMap = {
  bestWeekend: (): JSX.Element => <LocalizedText message="checkPrices.title.bestDeal" />,
  today: (): JSX.Element => <LocalizedText message="checkPrices.title.today" />,
  tomorrowNight: (): JSX.Element => <LocalizedText message="checkPrices.title.tomorrowNight" />,
  thisWeekend: (): JSX.Element => <LocalizedText message="checkPrices.title.thisWeekend" />,
  nextWeekend: (): JSX.Element => <LocalizedText message="checkPrices.title.nextWeekend" />
};

const defaultKeys = ["bestWeekend", "today", "thisWeekend", "nextWeekend"];
const checkPricesV3Keys = ["today", "tomorrowNight", "thisWeekend", "nextWeekend", "bestWeekend"];
export const a11yKeyToTextMap = {
  bestWeekend: (
    l10n: ILocalization,
    { regionName, datesLabel, regionType }: CheckPricesLocalizationProps
  ): string =>
    l10n.formatText(
      shouldUseNearPreposition(regionType)
        ? "checkPrices.title.bestDeal.title.closeTo"
        : "checkPrices.title.bestDeal.title",
      regionName,
      datesLabel
    ),
  today: (
    l10n: ILocalization,
    { regionName, datesLabel, regionType }: CheckPricesLocalizationProps
  ): string =>
    l10n.formatText(
      shouldUseNearPreposition(regionType)
        ? "checkPrices.title.today.title.closeTo"
        : "checkPrices.title.today.title",
      regionName,
      datesLabel
    ),
  tomorrowNight: (
    l10n: ILocalization,
    { regionName, datesLabel, regionType }: CheckPricesLocalizationProps
  ): string =>
    l10n.formatText(
      shouldUseNearPreposition(regionType)
        ? "checkPrices.title.tomorrowNight.title"
        : "checkPrices.title.tomorrowNight.title",
      regionName,
      datesLabel
    ), // TODO replace this with the a11y label for close to when we get it.
  thisWeekend: (
    l10n: ILocalization,
    { regionName, datesLabel, regionType }: CheckPricesLocalizationProps
  ): string =>
    l10n.formatText(
      shouldUseNearPreposition(regionType)
        ? "checkPrices.title.thisWeekend.title.closeTo"
        : "checkPrices.title.thisWeekend.title",
      regionName,
      datesLabel
    ),
  nextWeekend: (
    l10n: ILocalization,
    { regionName, datesLabel, regionType }: CheckPricesLocalizationProps
  ): string =>
    l10n.formatText(
      shouldUseNearPreposition(regionType)
        ? "checkPrices.title.nextWeekend.title.closeTo"
        : "checkPrices.title.nextWeekend.title",
      regionName,
      datesLabel
    )
};

function formatDate(date: string): string {
  return formatDateString(date, { skeleton: "MMMd" });
}

function buildDatesLabel(start: string, end: string): string {
  return start && end ? `${formatDate(start)} - ${formatDate(end)}` : "";
}

function shouldUseNearPreposition(regionType: string) {
  return !isPolygon(regionType);
}

const Deal = ({
  modelKey,
  model,
  searchContext,
  l10n
}: {
  modelKey: string;
  model: CheckPricesFlexModuleResult;
  searchContext: SearchContext;
  l10n: ILocalization;
}) => {
  if (modelKey !== "tomorrowNight" && (!modelKeyToDealTextMap[modelKey] || !model[modelKey])) {
    return null;
  }
  let content: Deal = model[modelKey];

  if (modelKey === "tomorrowNight") {
    const today = new Date();
    const tomorrow = new Date();
    const todayLink = model["today"].link; // grabs dateless
    tomorrow.setDate(today.getDate() + 1);
    const afterTomorrow = new Date();
    afterTomorrow.setDate(today.getDate() + 2);
    content = {
      link: `${todayLink}&startDate=${formatDateString(tomorrow, { raw: "yyyy-MM-dd" })}`,
      label: "", // this gets built later on using end and start dates. It's never used
      isoEndDate: formatDateString(afterTomorrow, { raw: "yyyy-MM-dd" }),
      isoStartDate: formatDateString(tomorrow, { raw: "yyyy-MM-dd" })
    };
  }

  let cardLink: string = content && content.link;
  if (modelKey === "today") {
    const today = new Date();
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    content.isoStartDate = formatDateString(today, { raw: "yyyy-MM-dd" });
    content.isoEndDate = formatDateString(tomorrow, { raw: "yyyy-MM-dd" });
    cardLink = `${cardLink}&startDate=${content.isoStartDate}`;
  }

  const regionName: string = searchContext && searchContext.location && searchContext.location.name;
  const regionType: string = searchContext && searchContext.location && searchContext.location.type;
  const datesLabel = buildDatesLabel(content.isoStartDate, content.isoEndDate);
  const linkText: string = a11yKeyToTextMap[modelKey](l10n, { regionName, datesLabel, regionType });

  return (
    <UitkCardTextSegment
      className="cardTextContainer all-y-padding-four"
      separator={UitkCardSectionSeparatorType.TOP}
      key={modelKey}
    >
      <span className="dealText uitk-type-300 all-y-padding-one" aria-hidden={true}>
        {modelKeyToDealTextMap[modelKey]()}
      </span>
      <span className="icon-right uitk-type-300 uitk-link" aria-hidden={true}>
        <a href={cardLink}>{datesLabel}</a>
      </span>

      <TrackedLink
        className="uitk-card-link"
        moduleName="check-prices"
        rfrr={modelKey}
        href={cardLink}
      >
        {linkText}
      </TrackedLink>
    </UitkCardTextSegment>
  );
};

export const CheckPrices = (props: CheckPricesProps) => {
  const context = props.context;
  const isCheckPricesV3Enabled = isVariantEnabled(context, Experiment.Flex_check_prices_version_3);
  const searchContext = context && context.searchContext;
  const flexComponent = props.flexComponent;
  const model = flexComponent.model;

  return (
    <div
      className="CheckPrices all-cell-1-1 uitk-cell all-y-padding-three"
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <UitkCard>
        <UitkCardContent>
          <UitkCardTextSegment className="all-y-padding-four">
            <h2 className="uitk-type-heading-600">{model.title}</h2>
          </UitkCardTextSegment>
          {(isCheckPricesV3Enabled ? checkPricesV3Keys : defaultKeys).map(modelKey => (
            <Deal
              key={checkPricesKey.next()}
              l10n={props.l10n}
              modelKey={modelKey}
              model={model}
              searchContext={searchContext}
            />
          ))}
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

export default CheckPrices;
